const ROLE_ORDER = ["signature", "headline", "body", "accent"];

const ROLE_LABELS = {
  signature: "Фирменное имя",
  headline: "Заголовки",
  body: "Основной текст",
  accent: "Акцент",
};

function supports(font, requirement) {
  if (!font) return false;
  if (requirement === "latin") return font.scripts?.includes("latin");
  return font.scripts?.includes("cyrillic") && (requirement !== "both" || font.scripts?.includes("latin"));
}

function isSystemGeneratedPreference(item) {
  const note = String(item?.note || "").trim().toLowerCase();
  return note.startsWith("типографическая система:") || note.startsWith("typography system:");
}

function preferencePriority(item) {
  let score = 0;
  if (item?.status !== "archived") score += 100;
  if (item?.origin === "user_explicit") score += 40;
  if (item?.mode === "choose_font" || item?.mode === "uploaded_font") score += 30;
  if (String(item?.note || "").trim() === "Принято из Font Pairing") score += 25;
  if (!isSystemGeneratedPreference(item)) score += 15;
  score += Math.min(Number(item?.order || 0), 999) / 1000;
  return score;
}

export function resolveTypographyPreferencesByRole(preferences = []) {
  const map = new Map();
  preferences
    .filter((item) => item.status !== "archived")
    .forEach((item) => {
      const current = map.get(item.role);
      if (!current || preferencePriority(item) > preferencePriority(current)) {
        map.set(item.role, item);
      }
    });
  return map;
}

function isConcretePreference(item) {
  return item?.mode === "choose_font" || item?.mode === "uploaded_font";
}

function manualDecisionPriority(item) {
  const order = Number(item?.order || 0);
  const explicitPairingAcceptance =
    String(item?.note || "").trim() === "Принято из Font Pairing";
  return (explicitPairingAcceptance ? 100000 : 0) + order;
}

export function resolveConfirmedConcreteByRole(preferences = []) {
  const map = new Map();
  preferences
    .filter((item) => item.status !== "archived")
    .filter(isConcretePreference)
    .filter((item) => item.origin === "user_explicit")
    .filter((item) => !isSystemGeneratedPreference(item))
    .forEach((item) => {
      const current = map.get(item.role);
      if (!current || manualDecisionPriority(item) > manualDecisionPriority(current)) {
        map.set(item.role, item);
      }
    });
  return map;
}

function anchorSignals(anchor) {
  const note = String(anchor?.note || "").toLowerCase();
  return {
    cleanModern: /(чист|современ|лаконич|воздух|между букв|tracking|печатн)/i.test(note),
    calm: /(спокой|сдержан|тих|деликат)/i.test(note),
    handwritten: /(рукопис|script|handwrit)/i.test(note),
    expressive: /(смел|выраз|драмат|характерн)/i.test(note),
  };
}

function concreteCatalogFont(item, byId) {
  return item?.font_catalog_id ? byId.get(item.font_catalog_id) || null : null;
}

function hasWeight(font, weight) {
  return (font?.weights || []).includes(weight);
}

function nearestWeight(font, wanted, fallback = 400) {
  const values = [...(font?.weights || [fallback])].sort((a, b) => Math.abs(a - wanted) - Math.abs(b - wanted));
  return values[0] || fallback;
}

export function roleTreatment(role, font, signals = {}) {
  if (role === "signature") {
    return {
      font_weight: nearestWeight(font, 500),
      font_style: "normal",
      letter_spacing_em: signals.cleanModern ? 0.12 : 0.04,
      text_transform: signals.cleanModern ? "uppercase" : "none",
    };
  }
  if (role === "headline") {
    return {
      font_weight: nearestWeight(font, 600),
      font_style: "normal",
      letter_spacing_em: 0,
      text_transform: "none",
    };
  }
  if (role === "body") {
    return {
      font_weight: nearestWeight(font, 400),
      font_style: "normal",
      letter_spacing_em: 0,
      text_transform: "none",
    };
  }
  return {
    font_weight: nearestWeight(font, 400),
    font_style: (font?.styles || []).includes("italic") ? "italic" : "normal",
    letter_spacing_em: 0.01,
    text_transform: "none",
  };
}

// Advisory candidates are detached proposals. Only the existing explicit whole-system
// save publishes them; manual preferences and private assets remain untouched.
export function adviseTypographySystem({ system, notes = "", requirement = "both" } = {}) {
  if (!system) return { recommendations: [], observations: [] };
  const observations = [];
  const candidates = [];
  const valid = (entry) => entry && !entry.font.uploaded && supports(entry.font, entry.language_requirement || requirement)
    && entry.font.weights?.includes(entry.treatment.font_weight)
    && entry.font.styles?.includes(entry.treatment.font_style);
  Object.entries(system.roles).forEach(([role, entry]) => {
    if (entry.font.uploaded) return; // Private font metadata cannot establish catalog suitability.
    if (!valid(entry)) observations.push(`${ROLE_LABELS[role]}: проверь доступность начертания и поддержку нужных языков.`);
  });
  const propose = (id, role, entry, reason) => {
    if (!valid(entry)) return;
    candidates.push({ ...system, id, title: "Рекомендую попробовать", reason,
      requires_replacement: true, roles: { ...system.roles, [role]: { ...entry, locked: false } } });
  };
  const signature = system.roles.signature;
  const headline = system.roles.headline;
  const body = system.roles.body;
  const signals = anchorSignals({ note: notes });
  if (valid(signature) && valid(headline)) {
    const heavier = signature.treatment.font_weight > headline.treatment.font_weight;
    const expanded = signature.treatment.letter_spacing_em >= 0.08;
    const uppercase = signature.treatment.text_transform === "uppercase";
    if (heavier || (signals.calm && (expanded || uppercase))) {
      propose("advisor-signature-treatment", "signature", { ...signature, treatment: {
        ...signature.treatment,
        font_weight: nearestWeight(signature.font, 400),
        letter_spacing_em: expanded ? 0.02 : signature.treatment.letter_spacing_em,
        text_transform: signals.calm && uppercase ? "none" : signature.treatment.text_transform,
      } }, heavier
        ? "Для имени выбрано более насыщенное начертание, чем для заголовка. Сравни с менее насыщенным вариантом на своём тексте."
        : "В имени используются прописные буквы или широкие интервалы. Сравни более сдержанное оформление со своим вариантом.");
      if (signature.font.id !== headline.font.id && ["sans_serif", "serif", "display"].includes(headline.font.category)) {
        propose("advisor-signature-headline", "signature", { ...headline, role: "signature", language_requirement: signature.language_requirement || requirement,
          treatment: { ...headline.treatment, letter_spacing_em: 0, text_transform: "none" } },
        uniqueFamilies({ ...system.roles, signature: headline }).size < uniqueFamilies(system.roles).size
          ? "Попробуй шрифт заголовка и в фирменном имени: станет на одну гарнитуру меньше. Сравни читаемость на своём имени."
          : "Попробуй шрифт заголовка и в фирменном имени. Сравни, как повторение его характера влияет на читаемость имени.");
      }
    }
  }
  if (valid(body)) {
    if (["display", "handwriting"].includes(body.font.category)) {
      observations.push("Основной текст набран акцентной гарнитурой. Проверь длинный абзац в небольшом размере.");
    }
    if (body.treatment.font_weight >= 600 || body.treatment.text_transform !== "none" || Math.abs(body.treatment.letter_spacing_em) > 0.04) {
      propose("advisor-body-treatment", "body", { ...body, treatment: roleTreatment("body", body.font) },
        "Для длинного текста попробуй обычный регистр, нейтральные интервалы и менее насыщенное начертание.");
    }
  }
  if (uniqueFamilies(system.roles).size > 3) observations.push("В сочетании больше трёх гарнитур. Проверь, есть ли у каждой отдельная задача.");
  const seen = new Set();
  deduplicateTypographySystems([system], seen);
  return { recommendations: deduplicateTypographySystems(candidates.filter((candidate) =>
    Object.values(candidate.roles).every((entry) => entry.font.uploaded || valid(entry))), seen), observations };
}

function utilityScore(font, requirement) {
  if (!supports(font, requirement)) return -Infinity;
  let score = 0;
  if (["sans_serif", "serif"].includes(font.category)) score += 30;
  if ((font.weights || []).length >= 4) score += 16;
  if ((font.styles || []).includes("italic")) score += 8;
  if (font.category === "display") score -= 20;
  if (font.category === "handwriting") score -= 35;
  if (font.category === "monospace") score -= 10;
  return score;
}

function anchorFamilyScore(font, requirement, signals) {
  if (!supports(font, requirement)) return -Infinity;
  let score = utilityScore(font, requirement);
  if (signals.cleanModern) {
    if (font.category === "sans_serif") score += 26;
    if (font.category === "serif") score -= 4;
    if (font.category === "handwriting") score -= 35;
  }
  if (signals.calm) {
    if (font.category === "sans_serif" || font.category === "serif") score += 6;
    if (font.category === "display") score -= 8;
  }
  if (signals.handwritten && font.category === "handwriting") score += 30;
  if (signals.expressive && ["display", "handwriting"].includes(font.category)) score += 16;
  return score;
}

function ranked(catalog, scorer, exclude = new Set()) {
  return (catalog || [])
    .filter((font) => !exclude.has(font.family))
    .map((font) => ({ font, score: scorer(font) }))
    .filter((entry) => Number.isFinite(entry.score))
    .sort((a, b) => b.score - a.score || a.font.family.localeCompare(b.font.family))
    .map((entry) => entry.font);
}

function uniqueFamilies(roles) {
  return new Set(Object.values(roles).map((role) => role?.font?.family).filter(Boolean));
}

function lockedRole(role, item, font) {
  if (!item) return null;
  if (font) {
    return {
      role,
      font,
      locked: true,
      source: "user_selected",
      treatment: {
        font_weight: item.font_weight ?? nearestWeight(font, role === "headline" ? 600 : role === "signature" ? 500 : 400),
        font_style: item.font_style || "normal",
        letter_spacing_em: item.letter_spacing_em ?? 0,
        text_transform: item.text_transform || "none",
      },
    };
  }
  if (item.mode === "uploaded_font") {
    return {
      role,
      font: {
        id: null,
        family: item.font_family || "Свой шрифт",
        category: "uploaded",
        scripts: [],
        weights: item.font_weight ? [item.font_weight] : [400],
        styles: item.font_style ? [item.font_style] : ["normal"],
        uploaded: true,
        font_asset_id: item.font_asset_id,
        font_asset_url: item.font_asset_url,
      },
      locked: true,
      source: "user_uploaded",
      treatment: {
        font_weight: item.font_weight ?? 400,
        font_style: item.font_style || "normal",
        letter_spacing_em: item.letter_spacing_em ?? 0,
        text_transform: item.text_transform || "none",
      },
    };
  }
  return null;
}

function withLocked(base, locked) {
  const out = { ...base };
  for (const [role, value] of locked.entries()) {
    if (value) out[role] = value;
  }
  return out;
}

function fillRole(roles, role, font, signals, reason) {
  if (roles[role]) return roles;
  return {
    ...roles,
    [role]: {
      role,
      font,
      locked: false,
      source: "system_proposed",
      treatment: roleTreatment(role, font, signals),
      reason,
    },
  };
}

function profileSummary(roles) {
  const families = [...uniqueFamilies(roles)];
  return {
    family_count: families.length,
    families,
  };
}

function systemOneFamily({ baseFamily, locked, signals }) {
  let roles = withLocked({}, locked);
  for (const role of ROLE_ORDER) {
    roles = fillRole(
      roles,
      role,
      baseFamily,
      signals,
      role === "signature"
        ? "Одна гарнитура сохраняет единый голос; характер имени создаётся прописными буквами, межбуквенным интервалом и насыщенностью."
        : role === "headline"
          ? "Заголовки строят иерархию внутри той же гарнитуры через размер и насыщенность."
          : role === "body"
            ? "Основной текст остаётся самым спокойным уровнем той же гарнитуры."
            : "Акцент использует курсив или другую насыщенность этой же гарнитуры вместо нового шрифта.",
    );
  }
  const anchorAligned = !signals.cleanModern || baseFamily.category === "sans_serif";
  return {
    id: "cohesive_one_family",
    score: anchorAligned ? 82 : 58,
    title: "Один типографический голос",
    principle: "Одна гарнитура, разные роли через насыщенность, курсив, масштаб и межбуквенный интервал.",
    fit_reason: anchorAligned
      ? "Самый цельный и дисциплинированный вариант: бренд говорит одним типографическим голосом, а иерархия создаётся внутри гарнитуры."
      : "Этот вариант максимально цельный, но строит весь бренд вокруг уже выбранной рабочей гарнитуры, а не вокруг характера визуального Typography Anchor.",
    tradeoff: anchorAligned
      ? "Меньше контраста между ролями. Узнаваемость должна рождаться из композиции, масштаба и межбуквенного интервала, а не из второй гарнитуры."
      : "Фирменное имя и заголовки могут оказаться дальше от твоего запроса на чистый современный печатный характер.",
    roles,
    ...profileSummary(roles),
  };
}

function systemCharacterUtility({ characterFamily, utilityFamily, locked, signals }) {
  let roles = withLocked({}, locked);
  roles = fillRole(
    roles,
    "signature",
    characterFamily,
    signals,
    "Фирменное имя несёт характер бренда. Здесь допустима более заметная гарнитура, потому что текст короткий и крупный.",
  );
  roles = fillRole(
    roles,
    "headline",
    characterFamily,
    signals,
    "Заголовки продолжают характер фирменного имени, чтобы не вводить третий визуальный голос без необходимости.",
  );
  roles = fillRole(
    roles,
    "body",
    utilityFamily,
    signals,
    "Для основного текста используется рабочая гарнитура: приоритет — кириллица, читаемость, диапазон насыщенностей и стабильность в длинном тексте.",
  );
  const accentBase = (characterFamily.styles || []).includes("italic") ? characterFamily : utilityFamily;
  roles = fillRole(
    roles,
    "accent",
    accentBase,
    signals,
    "Для акцента не обязателен третий шрифт: сначала проверяем курсив или другую насыщенность уже выбранного фирменного голоса.",
  );
  return {
    id: "character_plus_utility",
    score: signals.cleanModern && characterFamily.category === "sans_serif" ? 94 : 86,
    title: "Характер + читаемость",
    principle: "Одна гарнитура отвечает за узнаваемость, вторая — за рабочий текст.",
    fit_reason: signals.cleanModern && characterFamily.category === "sans_serif"
      ? "Позволяет сохранить твой чистый печатный характер в имени и заголовках, а уже выбранный основной текст оставить отдельным рабочим текстовым голосом."
      : "Разделяет две задачи: характер бренда и удобство длинного текста. Это классическая профессиональная модель характер + читаемость.",
    tradeoff: "Нужно жёстко соблюдать роли: фирменная гарнитура не должна расползаться в длинный текст, а рабочая — подменять фирменный голос.",
    roles,
    ...profileSummary(roles),
  };
}

function systemEditorialContrast({ serifFamily, sansFamily, locked, signals }) {
  let roles = withLocked({}, locked);
  const anchorWantsSans = signals.cleanModern;
  const identityFamily = anchorWantsSans ? sansFamily : serifFamily;
  const textFamily = anchorWantsSans ? serifFamily : sansFamily;

  roles = fillRole(
    roles,
    "signature",
    identityFamily,
    signals,
    "Фирменное имя следует явному характеру типографического ориентира, а не случайной категории.",
  );
  roles = fillRole(
    roles,
    "headline",
    identityFamily,
    signals,
    "Заголовки остаются в одном фирменном голосе с именем бренда.",
  );
  roles = fillRole(
    roles,
    "body",
    textFamily,
    signals,
    "Основной текст намеренно контрастирует с фирменной гарнитурой, чтобы разделить выразительную и информационную функции.",
  );
  roles = fillRole(
    roles,
    "accent",
    identityFamily,
    signals,
    "Акцент использует уже существующий фирменный голос; отдельная третья гарнитура не добавляется без пользовательского основания.",
  );

  return {
    id: "editorial_contrast",
    score: 84,
    title: "Редакционный контраст",
    principle: "Две гарнитуры с чётким разделением: фирменный голос и текстовый голос.",
    fit_reason: "Даёт более заметную иерархию между короткими фирменными сообщениями и длинным чтением, оставаясь в пределах двух гарнитур.",
    tradeoff: "Контраст становится частью идентичности. Если использовать гарнитуры не по назначенным ролям, система быстро потеряет цельность.",
    roles,
    ...profileSummary(roles),
  };
}

function systemExpressive({ identityFamily, utilityFamily, accentFamily, locked, signals }) {
  let roles = withLocked({}, locked);
  roles = fillRole(roles, "signature", identityFamily, signals, "Фирменное имя несёт главный характер бренда.");
  roles = fillRole(roles, "headline", identityFamily, signals, "Заголовки поддерживают основной фирменный голос.");
  roles = fillRole(roles, "body", utilityFamily, signals, "Основной текст остаётся нейтральным и функциональным.");
  roles = fillRole(
    roles,
    "accent",
    accentFamily,
    signals,
    "Третья гарнитура разрешена только потому, что пользователь явно запросил отдельный выразительный или рукописный голос.",
  );

  return {
    id: "expressive_third_voice",
    score: signals.handwritten || signals.expressive ? 88 : 60,
    title: "Третий выразительный голос",
    principle: "Третья гарнитура появляется только при явной смысловой задаче для акцента.",
    fit_reason: "Отдельный акцентный голос оправдан пользовательским запросом и работает только в коротких фразах или специальных носителях.",
    tradeoff: "Самая сложная система: третий голос требует строгих границ применения, иначе типографика станет пёстрой.",
    roles,
    ...profileSummary(roles),
  };
}


function lockedFamilySet(locked) {
  return new Set(
    [...locked.values()]
      .map((entry) => entry?.font?.family)
      .filter(Boolean),
  );
}

function replacementList(roles, locked) {
  const replacements = [];
  for (const role of ROLE_ORDER) {
    const lockedEntry = locked.get(role);
    const proposed = roles[role];
    if (!lockedEntry?.font?.family || !proposed?.font?.family) continue;
    if (lockedEntry.font.family !== proposed.font.family) {
      replacements.push({
        role,
        role_label: ROLE_LABELS[role],
        from: lockedEntry.font.family,
        to: proposed.font.family,
      });
    }
  }
  return replacements;
}

function oneFamilyAlternative({ family, locked, signals }) {
  if (!family) return null;
  let roles = {};
  for (const role of ROLE_ORDER) {
    roles = fillRole(
      roles,
      role,
      family,
      signals,
      role === "signature"
        ? "Фирменное имя строится на одной гарнитуре через прописные буквы, межбуквенный интервал и насыщенность."
        : role === "headline"
          ? "Заголовки используют ту же гарнитуру и меняют иерархию размером и насыщенностью."
          : role === "body"
            ? "Основной текст тоже переводится в эту гарнитуру, поэтому такой вариант требует отдельного подтверждения."
            : "Акцент использует курсив или другую насыщенность без добавления новой гарнитуры.",
    );
  }
  const replacements = replacementList(roles, locked);
  if (!replacements.length) return null;
  return {
    id: "alternative_one_family",
    score: 0,
    alternative: true,
    requires_replacement: true,
    replacements,
    title: "Альтернатива: один типографический голос",
    principle: "Одна гарнитура для всех ролей — только если ты готова заменить уже подтверждённый выбор.",
    fit_reason: "Вариант показывает, как выглядел бы бренд с максимально цельным единым голосом.",
    tradeoff: "Для этого придётся отменить одно или несколько уже подтверждённых типографических решений.",
    roles,
    ...profileSummary(roles),
  };
}

export function buildTypographySystems({
  catalog = [],
  preferences = [],
  legacyAnchor = null,
  requirement = "both",
}) {
  const byId = new Map(catalog.map((font) => [font.id, font]));
  const roleMap = resolveTypographyPreferencesByRole(preferences);
  const confirmedConcrete = resolveConfirmedConcreteByRole(preferences);
  const signals = anchorSignals(legacyAnchor);
  const locked = new Map();

  for (const role of ROLE_ORDER) {
    const item = confirmedConcrete.get(role) || roleMap.get(role);
    locked.set(role, lockedRole(role, item, concreteCatalogFont(item, byId)));
  }

  const lockedFamilies = lockedFamilySet(locked);
  const selectedBody = locked.get("body")?.font || null;
  const selectedIdentity = locked.get("signature")?.font || locked.get("headline")?.font || null;

  const utility = ranked(
    catalog,
    (font) => utilityScore(font, requirement),
  );
  const anchorRanked = ranked(
    catalog,
    (font) => anchorFamilyScore(font, requirement, signals),
  );
  const serif = ranked(
    catalog.filter((font) => font.category === "serif"),
    (font) => utilityScore(font, requirement),
  );
  const sans = ranked(
    catalog.filter((font) => font.category === "sans_serif"),
    (font) => anchorFamilyScore(font, requirement, signals),
  );

  // A private file remains locked to its requested role. Its glyph coverage and
  // other weights have not been catalogued, so do not spread it to new roles.
  const baseFamily = (supports(selectedBody, requirement) && selectedBody) || (supports(selectedIdentity, requirement) && selectedIdentity) || anchorRanked[0] || utility[0];
  const characterFamily = (supports(selectedIdentity, requirement) && selectedIdentity) || sans[0] || anchorRanked[0] || baseFamily;
  const utilityFamily = (supports(selectedBody, requirement) && selectedBody) || utility.find((font) => font.family !== characterFamily?.family) || baseFamily;
  const serifFamily = selectedBody?.category === "serif" ? selectedBody : serif[0] || baseFamily;
  const sansFamily = selectedIdentity?.category === "sans_serif" ? selectedIdentity : sans[0] || baseFamily;

  const systems = [];
  if (baseFamily && lockedFamilies.size <= 1) {
    systems.push(systemOneFamily({ baseFamily, locked, signals }));
  }
  if (characterFamily && utilityFamily) {
    systems.push(systemCharacterUtility({
      characterFamily,
      utilityFamily,
      locked,
      signals,
    }));
  }
  if (serifFamily && sansFamily && serifFamily.family !== sansFamily.family) {
    systems.push(systemEditorialContrast({
      serifFamily,
      sansFamily,
      locked,
      signals,
    }));
  }

  if (signals.handwritten || signals.expressive) {
    const accentCandidates = ranked(
      catalog.filter((font) => ["handwriting", "display"].includes(font.category)),
      (font) => {
        let score = anchorFamilyScore(font, requirement, signals);
        if (signals.handwritten && font.category === "handwriting") score += 30;
        if (signals.expressive && font.category === "display") score += 20;
        return score;
      },
      lockedFamilies,
    );
    if (accentCandidates[0] && characterFamily && utilityFamily) {
      systems.push(systemExpressive({
        identityFamily: characterFamily,
        utilityFamily,
        accentFamily: accentCandidates[0],
        locked,
        signals,
      }));
    }
  }

  const deduped = [];
  const seen = new Set();
  for (const system of systems) {
    const signature = ROLE_ORDER.map((role) => {
      const item = system.roles[role];
      return [
        item?.font?.family || "",
        item?.treatment?.font_weight || "",
        item?.treatment?.font_style || "",
        item?.treatment?.letter_spacing_em || 0,
        item?.treatment?.text_transform || "none",
      ].join(":");
    }).join("|");
    if (!seen.has(signature)) {
      seen.add(signature);
      deduped.push(system);
    }
  }


  const compatibleSystems = [];
  const conflictingSystems = [];

  for (const system of deduped) {
    const replacements = replacementList(system.roles, locked);
    if (replacements.length) {
      conflictingSystems.push({
        ...system,
        id: `alternative_${system.id}`,
        alternative: true,
        requires_replacement: true,
        replacements,
        title: system.title.startsWith("Альтернатива:")
          ? system.title
          : `Альтернатива: ${system.title}`,
        principle: `${system.principle} Этот вариант требует заменить подтверждённое решение.`,
      });
    } else {
      compatibleSystems.push(system);
    }
  }

  const alternatives = [...conflictingSystems];
  const alternativeIdentityFamily = sans[0] || anchorRanked[0] || utility[0];
  const oneVoiceAlternative = oneFamilyAlternative({
    family: alternativeIdentityFamily,
    locked,
    signals,
  });
  if (oneVoiceAlternative) {
    const duplicate = alternatives.some((item) =>
      (item.replacements || []).map((r) => `${r.role}:${r.from}->${r.to}`).join("|") ===
      (oneVoiceAlternative.replacements || []).map((r) => `${r.role}:${r.from}->${r.to}`).join("|")
    );
    if (!duplicate) alternatives.push(oneVoiceAlternative);
  }

  const evidence = {
    anchor_note: legacyAnchor?.note || "",
    anchor_is_visual_reference: legacyAnchor?.mode === "provided",
    explicit_signals: signals,
    locked_roles: ROLE_ORDER
      .filter((role) => locked.get(role))
      .map((role) => ({
        role,
        family: locked.get(role).font.family,
        source: locked.get(role).source,
        confirmed_manual: Boolean(confirmedConcrete.get(role)),
      })),
  };

  const rankedSystems = compatibleSystems
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .slice(0, 3)
    .map((system, index) => ({
      ...system,
      recommended: index === 0,
    }));

  return {
    systems: rankedSystems,
    alternatives: alternatives
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, 2),
    evidence,
    requirement,
  };
}

// Call after the presentation has finalized its visible roles. Keep the first
// ranked recommendation, and share seen across compatible/replacement options.
export function deduplicateTypographySystems(systems, seen = new Set()) {
  return systems.filter((system) => {
    const signature = JSON.stringify(ROLE_ORDER.map((role) => {
      const entry = system.roles[role];
      if (!entry) return null;
      const { font, treatment = {} } = entry;
      const identity = font.uploaded
        ? ["uploaded", font.font_asset_id || font.font_asset_url || font.family]
        : ["catalog", font.id || font.family];
      return [role, identity, treatment.font_weight ?? 400,
        treatment.font_style || "normal", treatment.letter_spacing_em ?? 0,
        treatment.text_transform || "none"];
    }));
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

export { ROLE_ORDER, ROLE_LABELS };

